import type { Home, ResidentBody } from '@lmc/ts-common'
import { Button, EmptyState, Form, PageHeader } from '@lmc/ui-kit'
import type {
  EthnicityCode,
  MaritalStatusCode,
  PregnancyStatusCode,
  ReligionCode,
  ResidentDemographicsUpdate,
  ResidentFetchItemResponse,
  ResidentUpdateRequest,
  SexualOrientationCode
} from '@lmc/validations'
import { Form as AntdForm } from 'antd'
import { getNominative, showToast } from 'common/utils'
import { useCanEditServiceUserData } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import useResident, {
  convertFormTypeToResidentBody,
  type ResidentItemExtended
} from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ButtonsWrapper,
  ProfileContent,
  ProfileWrapper
} from '../../styledComponents'
import { getAge, handleOnFinishFailed, scrollToTop } from '../../utils/utils'
import { CarerPreferenceFormCard } from './FormCards/CarerPreference'
import { CircumstancesFormCard } from './FormCards/Circumstances'
import { CustomIdFormCard } from './FormCards/CustomId'
import { DemographicFormCard } from './FormCards/Demographic'
import EditProfilePictureFormCard from './FormCards/EditProfilePicture'
import { NameFormCard } from './FormCards/Name'
import { PersonalNotesFormCard } from './FormCards/PersonalNotes'

export type FormType = {
  circumstances?: {
    educationalHistory: { description: string }
    occupationalHistory: { description: string }
    socialCircumstances: { description: string }
  }
  customId?: string
  dateOfBirth: dayjs.Dayjs
  summary?: string
  demographics?: ResidentDemographicsUpdate
  ethnicity?: EthnicityCode
  maritalStatus?: MaritalStatusCode
  pregnancyStatus?: PregnancyStatusCode
  religion?: ReligionCode
  sexualOrientation?: SexualOrientationCode
}

export const ResidentPersonalInformation = ({
  testId
}: {
  testId?: string
} = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const topRef = useRef<HTMLDivElement | null>(null)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const [isDirty, setIsDirty] = useState(false)
  const { id } = useParams()
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const { resident, isLoading, updateResident } = useResident({
    id,
    onUpdateSuccess: () => {
      showToast({
        title: t(supConstants.RESIDENT_UPDATED, {
          variable: residentNominative
        })
      })
      navigate(`../profile`, { relative: 'path' })
    }
  })
  const [residentAge, setResidentAge] = useState(getAge(resident?.dateOfBirth))
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const { isPro } = useHomeType()
  const featureFlags = useFeatureFlags({
    flagNames: ['mods_demographics', 'profile_rich_text']
  })?.data
  const isModsDemographicsEnabled = featureFlags?.mods_demographics?.enabled
  const richTextEnabled = featureFlags?.profile_rich_text?.enabled ?? false

  // Wholesale gate for the form. The sensitive status control lives in its own
  // modal and is gated separately. See CM-3521.
  const hasRolePermission = useCanEditServiceUserData()

  const handleOnFinish = async (values: FormType) => {
    setIsUpdateLoading(true)
    try {
      const convertedValues = convertFormTypeToResidentBody(
        values,
        (vals) =>
          convertFormTypeToPartialResidentBody(
            vals,
            (resident as any)?.demographics
          ) as Partial<ResidentBody>
      )
      setIsDirty(false)
      await updateResident(convertedValues)
    } catch {
      setIsDirty(true)
    }
    setIsUpdateLoading(false)
  }

  const handleOnCancel = () => {
    navigate(`../profile`, { relative: 'path' })
  }

  const handleFormValuesChange = (
    values: Partial<ResidentBody>,
    changedValues: Partial<ResidentBody>
  ) => {
    if (changedValues.dateOfBirth) {
      const age = getAge(changedValues.dateOfBirth)
      setResidentAge(age)
    }
    if (values) {
      setIsDirty(true)
    }
  }

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId ? `${testId}-personal-info-loading` : 'personal-info-loading'
        }
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId
            ? `${testId}-personal-info-no-resident`
            : 'personal-info-no-resident'
        }
      />
    )
  }

  const residentWithDemographics = resident as ResidentFetchItemResponse
  const r = resident as ResidentItemExtended
  const initialValues: Partial<FormType> = {
    ...resident,
    circumstances: {
      educationalHistory: {
        description: r.circumstances?.educationalHistory?.description ?? ''
      },
      occupationalHistory: {
        description: r.circumstances?.occupationalHistory?.description ?? ''
      },
      socialCircumstances: {
        description: r.circumstances?.socialCircumstances?.description ?? ''
      }
    },
    dateOfBirth: dayjs(resident?.dateOfBirth) || undefined,
    ...(isModsDemographicsEnabled && residentWithDemographics?.demographics
      ? {
          demographics: {
            genderIdentity:
              residentWithDemographics.demographics.genderIdentity ?? undefined,
            sex: residentWithDemographics.demographics.sex ?? undefined
          }
        }
      : {}),
    pregnancyStatus:
      (resident as any)?.demographics?.pregnancyStatus ?? undefined,
    sexualOrientation:
      (resident as any)?.demographics?.sexualOrientation ?? undefined,
    maritalStatus: (resident as any)?.demographics?.maritalStatus ?? undefined,
    religion: (resident as any)?.demographics?.religion ?? undefined,
    ethnicity: (resident as any)?.demographics?.ethnicity ?? undefined
  }

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={
        testId ? `${testId}-personal-info-wrapper` : 'personal-info-wrapper'
      }
    >
      <PageHeader
        sticky
        title={t(supConstants.PERSONAL_INFORMATION)}
        data-testid={
          testId ? `${testId}-personal-info-header` : 'personal-info-header'
        }
      >
        <ButtonsWrapper
          data-testid={
            testId ? `${testId}-personal-info-buttons` : 'personal-info-buttons'
          }
        >
          <Button
            data-cy="cancel-btn"
            onClick={handleOnCancel}
            data-testid={
              testId
                ? `${testId}-personal-info-cancel-btn`
                : 'personal-info-cancel-btn'
            }
          >
            {hasRolePermission ? t(supConstants.CANCEL) : t(supConstants.BACK)}
          </Button>
          <Button
            data-cy="submit-btn"
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isUpdateLoading}
            disabledByPermission={!hasRolePermission}
            data-testid={
              testId
                ? `${testId}-personal-info-save-btn`
                : 'personal-info-save-btn'
            }
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent
        data-testid={
          testId ? `${testId}-personal-info-content` : 'personal-info-content'
        }
      >
        <NavigationModal canNavigate={!isDirty} />
        <EditProfilePictureFormCard />
        <Form<FormType>
          form={form}
          disabled={!hasRolePermission}
          onFinish={handleOnFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          data-testid={
            testId ? `${testId}-personal-info-form` : 'personal-info-form'
          }
        >
          <NameFormCard />
          <DemographicFormCard
            residentAge={residentAge}
            isPro={isPro}
            resident={resident as ResidentFetchItemResponse}
          />
          <CustomIdFormCard />
          {isPro && <CarerPreferenceFormCard />}
          <CircumstancesFormCard
            disabled={!hasRolePermission}
            resident={r}
            richTextEnabled={richTextEnabled}
          />
          <PersonalNotesFormCard
            disabled={!hasRolePermission}
            richTextEnabled={richTextEnabled}
          />
        </Form>
      </ProfileContent>
    </ProfileWrapper>
  )
}

export const convertFormTypeToPartialResidentBody = (
  values: FormType,
  currentDemographics?: Record<string, unknown>
): Partial<ResidentUpdateRequest> & {
  circumstances?: FormType['circumstances']
} => {
  const {
    circumstances,
    demographics,
    ethnicity,
    maritalStatus,
    pregnancyStatus,
    religion,
    sexualOrientation,
    ...rest
  } = values as FormType & {
    ethnicity?: string
    maritalStatus?: string
    pregnancyStatus?: string
    religion?: string
    sexualOrientation?: string
  }

  return {
    ...rest,
    circumstances,
    // we need to send the date in the format 'DD-MM-YYYY' because of some legacy body parser code in BE
    dateOfBirth: values?.dateOfBirth.format('DD-MM-YYYY'),
    demographics: {
      ...currentDemographics,
      ...demographics,
      ethnicity: ethnicity ?? null,
      maritalStatus: maritalStatus ?? null,
      pregnancyStatus: pregnancyStatus ?? null,
      religion: religion ?? null,
      sexualOrientation: sexualOrientation ?? null
    }
  }
}
